import { Link, useParams } from 'react-router-dom'
import { useI18n } from '../i18n'
import { useAuth } from '../auth/AuthProvider'
import { usePublicHorse, usePublicNews, useFollowing, useFollow } from '../api/hooks'
import { PageSpinner } from '../components/ui'
import { ageFromDob, fmtDate } from '../lib/format'
import { toast } from '../lib/toast'

export function PublicProfile() {
  const { id = '' } = useParams()
  const { t, td, locale } = useI18n()
  const { user } = useAuth()
  const { data: horse, isLoading, isError } = usePublicHorse(id)
  const { data: news } = usePublicNews(id)
  const { data: following } = useFollowing(!!user)
  const follow = useFollow()

  if (isLoading) return <PageSpinner />
  if (isError || !horse) {
    return (
      <main className="mx-auto max-w-md px-5 py-24 text-center">
        <div className="card p-10">
          <div className="text-5xl">🔒</div>
          <h1 className="mt-4 text-2xl font-bold">{t('public.privateTitle')}</h1>
          <p className="mt-2 text-brand-600 dark:text-brand-300">{t('public.privateBody')}</p>
          <Link to="/discover" className="btn-primary mt-6">{t('public.backDiscover')}</Link>
        </div>
      </main>
    )
  }

  const isFollowing = !!following?.some((h) => h.id === horse.id)
  const age = ageFromDob(horse.dob)
  const share = () => {
    navigator.clipboard?.writeText(window.location.href)
    toast.success(t('public.linkCopied'))
  }

  return (
    <main className="mx-auto max-w-4xl px-5 py-8">
      <div className="mb-4 flex items-center justify-between">
        <span className="chip">{t('public.badge')}</span>
        <button onClick={share} className="btn-ghost !px-4 !py-2 text-xs">{t('public.share')}</button>
      </div>

      <div className="overflow-hidden rounded-3xl shadow-lift bg-brand-100 dark:bg-brand-800">
        {horse.profilePhotoUrl && <img src={horse.profilePhotoUrl} alt={horse.name} className="h-64 w-full object-cover sm:h-80" />}
      </div>

      <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">{horse.name}</h1>
          <p className="mt-1 text-brand-600 dark:text-brand-300">
            {[horse.breed, td('gender', horse.gender), age != null && `${age} ${t('units.yrs')}`, horse.colour && td('colour', horse.colour)].filter(Boolean).join(' · ')}
          </p>
          <p className="mt-1 text-sm text-brand-500 dark:text-brand-400">❤ {horse.followerCount} {t('horse.followers')}</p>
        </div>
        {user ? (
          <button
            onClick={() => follow.mutate({ id: horse.id, following: isFollowing })}
            disabled={follow.isPending}
            className={`${isFollowing ? 'btn-ghost' : 'btn-primary'} disabled:opacity-60`}
          >
            {isFollowing ? t('public.following') : t('public.follow')}
          </button>
        ) : (
          <Link to="/signin" className="btn-primary">{t('public.signInToFollow')}</Link>
        )}
      </div>

      {horse.about && <p className="mt-5 max-w-2xl leading-relaxed text-brand-700 dark:text-brand-200">{horse.about}</p>}

      <div className="mt-3 flex flex-wrap gap-2">
        {horse.discipline && <span className="chip">{td('discipline', horse.discipline)}</span>}
        {horse.country && <span className="chip">📍 {[horse.city, horse.country].filter(Boolean).join(', ')}</span>}
      </div>

      {horse.galleryUrls && horse.galleryUrls.length > 0 && (
        <>
          <h2 className="mt-10 mb-4 text-xl font-bold">{t('public.photos')}</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {horse.galleryUrls.map((url, i) => (
              <img key={i} src={url} alt="" loading="lazy" className="aspect-square w-full rounded-2xl object-cover shadow-soft" />
            ))}
          </div>
        </>
      )}

      {news && news.length > 0 && (
        <>
          <h2 className="mt-10 mb-4 text-xl font-bold">{t('public.latestNews')}</h2>
          <div className="space-y-4">
            {news.map((p) => (
              <article key={p.id} className="card overflow-hidden sm:flex">
                {p.imageUrls?.[0] && <img src={p.imageUrls[0]} alt="" className="h-40 w-full object-cover sm:h-auto sm:w-48" />}
                <div className="p-5">
                  <h3 className="font-bold">{p.title}</h3>
                  <div className="text-xs text-brand-500 dark:text-brand-400">{fmtDate(p.createdAt, locale)}</div>
                  <p className="mt-2 text-sm text-brand-700 dark:text-brand-200">{p.body}</p>
                </div>
              </article>
            ))}
          </div>
        </>
      )}

      <p className="mt-10 rounded-2xl bg-brand-100/60 dark:bg-brand-800/40 p-4 text-center text-sm text-brand-600 dark:text-brand-300">
        {t('public.privateNote')}
      </p>
    </main>
  )
}
